import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anonymous, obj = logged in
  const [permissions, setPermissions] = useState({}); // capability -> bool

  const loadPermissions = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/permissions");
      setPermissions(data.permissions || {});
    } catch {
      setPermissions({});
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await loadPermissions();
      } catch {
        setUser(false);
        setPermissions({});
      }
    })();
  }, [loadPermissions]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    await loadPermissions();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false);
    setPermissions({});
  };

  const can = useCallback((capability) => {
    if (!user) return false;
    if (user.role === "superadmin") return true;
    return Boolean(permissions[capability]);
  }, [user, permissions]);

  return (
    <AuthCtx.Provider value={{ user, setUser, login, logout, permissions, can, refreshPermissions: loadPermissions }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
export const usePermissions = () => {
  const ctx = useContext(AuthCtx);
  return { can: ctx?.can || (() => false), permissions: ctx?.permissions || {}, refresh: ctx?.refreshPermissions };
};

export function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-slate-500">
        Loading EliteOS…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}
